import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  LogOut,
  Plus,
  Trash2,
  Edit2,
  X,
  Menu,
  ShieldCheck,
  MessageSquareHeart,
  GraduationCap,
  FileText,
  ListOrdered,
  MessageCircle,
  Bot,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AuroraMark from "@/components/AuroraMark";
import ProductsTab from "@/components/admin/ProductsTab";
import PartnershipTab from "@/components/admin/PartnershipTab";
import FeedbackTab from "@/components/admin/FeedbackTab";
import SecurityTab from "@/components/admin/SecurityTab";
import ELearningTab from "@/components/admin/ELearningTab";
import CMSTab from "@/components/admin/CMSTab";
import NavTab from "@/components/admin/NavTab";
import TestimonialsTab from "@/components/admin/TestimonialsTab";
import AIKnowledgeTab from "@/components/admin/AIKnowledgeTab";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "products", label: "Produk", icon: ShoppingBag },
  { key: "partnership", label: "Mitra", icon: Users },
  { key: "feedback", label: "Kritik & Saran", icon: MessageSquareHeart },
  { key: "testimonials", label: "Testimoni", icon: MessageCircle },
  { key: "aiknowledge", label: "AI Knowledge", icon: Bot },
  { key: "elearning", label: "E-Learning", icon: GraduationCap },
  { key: "cms", label: "CMS Konten", icon: FileText },
  { key: "nav", label: "Menu Navigasi", icon: ListOrdered },
  { key: "security", label: "Keamanan", icon: ShieldCheck },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadStats = async () => {
    try {
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (user === false) navigate("/akses-internal-aurora");
    if (user) loadStats();
  }, [user, navigate]);

  if (user === null || user === false) {
    return <div className="p-10">Memuat…</div>;
  }

  const label = TABS.find((t) => t.key === tab)?.label || "Dashboard";

  return (
    <div className="min-h-screen md:flex bg-[#FDFBF7]" data-testid="admin-dashboard">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`fixed md:sticky top-0 left-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col z-50 transition-transform duration-300 overflow-y-auto ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="p-6 border-b border-stone-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AuroraMark size="h-12 w-12" />
            <div>
              <div className="font-display text-lg">Aurora <span className="text-[#F39237]">Hijab</span></div>
              <div className="overline text-[#F39237]">Admin</div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 text-stone-400 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`sidebar-${t.key}`}
              onClick={() => {
                setTab(t.key);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                tab === t.key
                  ? "bg-[#F39237] text-[#0A0A0A]"
                  : "text-stone-300 hover:bg-stone-900"
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </nav>

        <div className="p-6 border-t border-stone-800">
          <div className="text-xs text-stone-400 mb-2 truncate">{user.email}</div>
          <button
            data-testid="logout-button"
            onClick={() => {
              logout();
              navigate("/akses-internal-aurora");
            }}
            className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-[#F39237]"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="px-4 sm:px-6 md:px-10 py-4 md:py-6 border-b border-stone-200 flex items-center gap-3 sticky top-0 bg-[#FDFBF7] z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 -ml-2 text-stone-700"
            aria-label="Open sidebar"
            data-testid="sidebar-toggle"
          >
            <Menu size={22} />
          </button>
          <div className="min-w-0">
            <div className="overline text-[#F39237]">Aurora Admin</div>
            <h1 className="font-display text-xl md:text-2xl truncate">{label}</h1>
          </div>
        </header>

        <div className="p-4 sm:p-6 md:p-10">
          {tab === "overview" && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="stats-grid">
              <StatCard label="Total Produk" value={stats.total_products} />
              <StatCard label="Total Mitra" value={stats.total_partners} />
              <StatCard label="Reseller" value={stats.total_reseller} />
              <StatCard label="Agen" value={stats.total_agen} />
              <StatCard label="Distributor" value={stats.total_distributor} />
              <StatCard label="Kritik/Saran Baru" value={stats.new_feedback} accent />
              <StatCard label="Total Kritik/Saran" value={stats.total_feedback} />
            </div>
          )}

          {tab === "products" && <ProductsTab onReload={loadStats} />}
          {tab === "partnership" && <PartnershipTab onReload={loadStats} />}
          {tab === "feedback" && <FeedbackTab onReload={loadStats} />}
          {tab === "elearning" && <ELearningTab />}
          {tab === "testimonials" && <TestimonialsTab />}
          {tab === "aiknowledge" && <AIKnowledgeTab />}
          {tab === "cms" && <CMSTab />}
          {tab === "nav" && <NavTab />}
          {tab === "security" && <SecurityTab />}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      className={`p-5 md:p-7 border ${
        accent ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-stone-200"
      }`}
    >
      <div className={`overline ${accent ? "text-[#F39237]" : "text-stone-500"}`}>{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-2">{value ?? 0}</div>
    </div>
  );
}
